/**
 * AddProductModal — single-product creation modal (extracted from the
 * previous "switch to bulk-add tab" UX). Lives on the Products tab so
 * sellers can quickly add one product without leaving the list.
 *
 * Includes the WholesaleSlabsEditor so bulk-discount slabs can be set
 * at creation time (was previously edit-only) AND a Rent toggle that
 * lets sellers mark the product as rentable in the same flow.
 */
import React, { useEffect, useState } from "react";
import { X, Save, Image as ImageIcon, Video as VideoIcon, Calendar } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import WholesaleSlabsEditor from "../WholesaleSlabsEditor";
import ColorChipEditor from "../ColorChipEditor";


export default function AddProductModal({
  stores, categories, productTypes, onClose, onCreated,
}) {
  const [form, setForm] = useState({
    name: "", description: "", price: "", category_id: "", product_type_id: "",
    store_id: stores?.[0]?.store_id || "",
    sizes: "", stock: "10", image: "", video_url: "", banner_url: "",
    gender: "unisex", delivery_days: "5",
    is_new_launch: false, launch_date: "",
    is_advance_booking: false,
    advance_booking_opens_at: "", advance_booking_closes_at: "",
    expected_ship_date: "",
    wholesale_slabs: [],
    // Colour variants — list of {name, hex}. Empty list = single-SKU.
    colors: [],
    // Rental fields
    rental_enabled: false,
    rental_per_day: "",
    rental_deposit: "",
    rental_min_days: "1",
    rental_max_days: "30",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  // Rental-eligible categories — fetched once. When `rental_enabled` is
  // ON we restrict the category dropdown to this list.
  const [rentalCats, setRentalCats] = useState([]);
  useEffect(() => {
    api.get("/rentals/categories")
      .then(({ data }) => setRentalCats(data || []))
      .catch(() => setRentalCats([]));
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // If the seller flips Rent ON and the currently-selected category
  // isn't rental-eligible, clear it so they have to repick.
  const toggleRent = (next) => {
    setForm(f => {
      const out = { ...f, rental_enabled: next };
      if (next && f.category_id) {
        const ok = rentalCats.some(c => c.category_id === f.category_id);
        if (!ok) { out.category_id = ""; out.product_type_id = ""; }
      }
      return out;
    });
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (!form.name.trim() || !form.price || !form.category_id || !form.store_id) {
      setErr("Name, store, category and price are required.");
      return;
    }
    if (form.rental_enabled) {
      const perDay = parseFloat(form.rental_per_day);
      if (!perDay || perDay <= 0) {
        setErr("Rental price per day is required when 'Available for rent' is on.");
        return;
      }
      const mn = parseInt(form.rental_min_days || "1", 10);
      const mx = parseInt(form.rental_max_days || "30", 10);
      if (mn > mx) {
        setErr("Minimum days must be less than or equal to maximum days.");
        return;
      }
    }
    setBusy(true); setErr("");
    try {
      const sizes = form.sizes.split(",").map(s => s.trim()).filter(Boolean);
      const stockPerSize = parseInt(form.stock || "10", 10);
      const stock = sizes.length
        ? Object.fromEntries(sizes.map(s => [s, stockPerSize]))
        : { default: stockPerSize };

      await api.post("/seller/product", {
        store_id: form.store_id,
        name: form.name.trim(),
        description: form.description.trim(),
        price: parseFloat(form.price),
        category_id: form.category_id,
        product_type_id: form.product_type_id || null,
        images: form.image ? [form.image.trim()] : [],
        video_url: form.video_url.trim() || null,
        banner_url: form.banner_url.trim() || null,
        sizes,
        stock,
        gender: form.gender,
        delivery_days: parseInt(form.delivery_days || "5", 10) || 5,
        is_new_launch: !!form.is_new_launch,
        launch_date: form.launch_date || null,
        is_advance_booking: !!form.is_advance_booking,
        advance_booking_opens_at: form.advance_booking_opens_at || null,
        advance_booking_closes_at: form.advance_booking_closes_at || null,
        expected_ship_date: form.expected_ship_date || null,
        wholesale_slabs: form.wholesale_slabs.length ? form.wholesale_slabs : null,
        colors: form.colors.length ? form.colors : [],
        rental_enabled: !!form.rental_enabled,
        rental_per_day: form.rental_enabled ? parseFloat(form.rental_per_day) : null,
        rental_deposit: form.rental_enabled ? parseFloat(form.rental_deposit || "0") : null,
        rental_min_days: form.rental_enabled ? parseInt(form.rental_min_days || "1", 10) : null,
        rental_max_days: form.rental_enabled ? parseInt(form.rental_max_days || "30", 10) : null,
      });
      onCreated();
    } catch (ex) {
      setErr(formatApiError(ex?.response?.data?.detail) || "Couldn't create product");
    } finally { setBusy(false); }
  };

  // Category dropdown source — restricted when Rent mode is on.
  const catSource = form.rental_enabled ? rentalCats : categories;
  const typesForCat = productTypes.filter(t => t.category_id === form.category_id);

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
      data-testid="add-product-modal">
      <div className="bg-[#1A0F1F] border border-[#3D2845] rounded-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 z-10 flex items-center gap-3 px-5 py-4 border-b border-[#3D2845] bg-[#1A0F1F]">
          <div className="flex-1">
            <div className="font-display font-black text-xl uppercase">Add product</div>
            <div className="text-[10px] text-[#A1A1AA]">Create a single product — full slabs &amp; pre-order options included.</div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg bg-[#3D2845] hover:bg-[#FF007F] hover:text-white flex items-center justify-center"
            data-testid="add-product-close-btn"
            aria-label="Close">
            <X size={14}/>
          </button>
        </div>

        <form onSubmit={submit} className="p-5 space-y-4">
          {err && <div className="text-xs text-[#FF007F] bg-[#FF007F]/10 border border-[#FF007F]/30 rounded-lg px-3 py-2"
            data-testid="add-product-error">{err}</div>}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block sm:col-span-2">
              <span className="block text-[10px] uppercase tracking-widest font-bold text-[#A1A1AA] mb-1">Product name *</span>
              <input className="input-dark text-sm w-full" value={form.name}
                data-testid="add-product-name"
                onChange={e => set("name", e.target.value)}/>
            </label>
            <label className="block sm:col-span-2">
              <span className="block text-[10px] uppercase tracking-widest font-bold text-[#A1A1AA] mb-1">Description</span>
              <textarea className="input-dark text-sm w-full" rows={2} value={form.description}
                data-testid="add-product-desc"
                onChange={e => set("description", e.target.value)}/>
            </label>
            <label className="block">
              <span className="block text-[10px] uppercase tracking-widest font-bold text-[#A1A1AA] mb-1">Store *</span>
              <select className="input-dark text-sm w-full" value={form.store_id}
                data-testid="add-product-store"
                onChange={e => set("store_id", e.target.value)}>
                {stores.map(s => <option key={s.store_id} value={s.store_id}>{s.name}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="block text-[10px] uppercase tracking-widest font-bold text-[#A1A1AA] mb-1">Price (USD) *</span>
              <input type="number" step="0.01" min="0" className="input-dark text-sm w-full" value={form.price}
                data-testid="add-product-price"
                onChange={e => set("price", e.target.value)}/>
            </label>
            <label className="block">
              <span className="block text-[10px] uppercase tracking-widest font-bold text-[#A1A1AA] mb-1">
                Category *{form.rental_enabled && <span className="ml-1 text-[#D4FF00]">· rental-eligible only</span>}
              </span>
              <select className="input-dark text-sm w-full" value={form.category_id}
                data-testid="add-product-category"
                onChange={e => { set("category_id", e.target.value); set("product_type_id", ""); }}>
                <option value="">— Choose —</option>
                {catSource.map(c => <option key={c.category_id} value={c.category_id}>{c.name}</option>)}
              </select>
              {form.rental_enabled && catSource.length === 0 && (
                <div className="text-[10px] text-[#FF007F] mt-1" data-testid="add-product-no-rental-cats">
                  No rental-eligible categories. Ask an admin to flag categories as rentable.
                </div>
              )}
            </label>
            <label className="block">
              <span className="block text-[10px] uppercase tracking-widest font-bold text-[#A1A1AA] mb-1">Type</span>
              <select className="input-dark text-sm w-full" value={form.product_type_id}
                data-testid="add-product-type"
                onChange={e => set("product_type_id", e.target.value)}>
                <option value="">— None —</option>
                {typesForCat.map(t => <option key={t.type_id} value={t.type_id}>{t.name}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="block text-[10px] uppercase tracking-widest font-bold text-[#A1A1AA] mb-1">Sizes (comma)</span>
              <input className="input-dark text-sm w-full" placeholder="S,M,L,XL"
                data-testid="add-product-sizes"
                value={form.sizes} onChange={e => set("sizes", e.target.value)}/>
            </label>
            <label className="block">
              <span className="block text-[10px] uppercase tracking-widest font-bold text-[#A1A1AA] mb-1">Stock / size</span>
              <input type="number" min="0" className="input-dark text-sm w-full" value={form.stock}
                data-testid="add-product-stock"
                onChange={e => set("stock", e.target.value)}/>
            </label>
            <label className="block">
              <span className="block text-[10px] uppercase tracking-widest font-bold text-[#A1A1AA] mb-1">Gender</span>
              <select className="input-dark text-sm w-full" value={form.gender}
                onChange={e => set("gender", e.target.value)}>
                <option value="unisex">Unisex</option><option value="men">Men</option>
                <option value="women">Women</option><option value="kids">Kids</option>
              </select>
            </label>
            <label className="block">
              <span className="block text-[10px] uppercase tracking-widest font-bold text-[#A1A1AA] mb-1">Delivery days</span>
              <input type="number" min="1" className="input-dark text-sm w-full" value={form.delivery_days}
                onChange={e => set("delivery_days", e.target.value)}/>
            </label>
            <label className="block sm:col-span-2">
              <span className="block text-[10px] uppercase tracking-widest font-bold text-[#A1A1AA] mb-1 flex items-center gap-1">
                <ImageIcon size={11}/>Image URL
              </span>
              <input className="input-dark text-sm w-full" placeholder="https://…"
                data-testid="add-product-image"
                value={form.image} onChange={e => set("image", e.target.value)}/>
            </label>
            <label className="block sm:col-span-2">
              <span className="block text-[10px] uppercase tracking-widest font-bold text-[#A1A1AA] mb-1 flex items-center gap-1">
                <VideoIcon size={11}/>Video URL
              </span>
              <input className="input-dark text-sm w-full" placeholder="https://…"
                value={form.video_url} onChange={e => set("video_url", e.target.value)}/>
            </label>
          </div>

          {/* Wholesale slabs */}
          <WholesaleSlabsEditor
            slabs={form.wholesale_slabs}
            onChange={(arr) => set("wholesale_slabs", arr)}
            compact/>

          {/* Colour variants — buyers see a swatch picker on product cards */}
          <ColorChipEditor
            colors={form.colors}
            onChange={(list) => set("colors", list)}
            testidPrefix="add-product-color"
            compact/>

          {/* Rental config — toggle + 4 inputs when ON. Category dropdown
              above auto-filters to rental-eligible categories when toggled. */}
          <div className="border border-[#3D2845] rounded-xl p-3 bg-[#261534]/50"
            data-testid="add-product-rental-section">
            <label className="flex items-center gap-2 text-xs cursor-pointer select-none">
              <input type="checkbox" checked={form.rental_enabled}
                data-testid="add-product-rental-toggle"
                onChange={e => toggleRent(e.target.checked)}/>
              <Calendar size={13} className="text-[#00F0FF]"/>
              <span className="font-bold uppercase tracking-widest text-[10px]">Available for rent</span>
              <span className="text-[#A1A1AA] text-[10px] normal-case font-normal">
                — let buyers rent this item by the day
              </span>
            </label>
            {form.rental_enabled && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3 pt-3 border-t border-[#3D2845]">
                <label className="block">
                  <span className="block text-[10px] uppercase tracking-widest font-bold text-[#A1A1AA] mb-1">$ / day *</span>
                  <input type="number" step="0.01" min="0" className="input-dark text-sm w-full"
                    data-testid="add-product-rental-per-day"
                    value={form.rental_per_day} onChange={e => set("rental_per_day", e.target.value)}/>
                </label>
                <label className="block">
                  <span className="block text-[10px] uppercase tracking-widest font-bold text-[#A1A1AA] mb-1">Deposit</span>
                  <input type="number" step="0.01" min="0" className="input-dark text-sm w-full"
                    data-testid="add-product-rental-deposit"
                    value={form.rental_deposit} onChange={e => set("rental_deposit", e.target.value)}/>
                </label>
                <label className="block">
                  <span className="block text-[10px] uppercase tracking-widest font-bold text-[#A1A1AA] mb-1">Min days</span>
                  <input type="number" min="1" max="365" className="input-dark text-sm w-full"
                    data-testid="add-product-rental-min-days"
                    value={form.rental_min_days} onChange={e => set("rental_min_days", e.target.value)}/>
                </label>
                <label className="block">
                  <span className="block text-[10px] uppercase tracking-widest font-bold text-[#A1A1AA] mb-1">Max days</span>
                  <input type="number" min="1" max="365" className="input-dark text-sm w-full"
                    data-testid="add-product-rental-max-days"
                    value={form.rental_max_days} onChange={e => set("rental_max_days", e.target.value)}/>
                </label>
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-[#3D2845]">
            <label className="flex items-center gap-1.5 text-xs cursor-pointer">
              <input type="checkbox" checked={form.is_new_launch}
                onChange={e => set("is_new_launch", e.target.checked)}/>
              🚀 New launch
            </label>
            {form.is_new_launch && (
              <input type="date" className="input-dark text-xs"
                value={form.launch_date} onChange={e => set("launch_date", e.target.value)}/>
            )}
            <label className="flex items-center gap-1.5 text-xs cursor-pointer">
              <input type="checkbox" checked={form.is_advance_booking}
                onChange={e => set("is_advance_booking", e.target.checked)}/>
              📦 Pre-order
            </label>
            {form.is_advance_booking && (
              <input type="date" className="input-dark text-xs" placeholder="Expected ship"
                value={form.expected_ship_date} onChange={e => set("expected_ship_date", e.target.value)}/>
            )}
          </div>

          <div className="flex items-center justify-end gap-2 pt-3 border-t border-[#3D2845] sticky bottom-0 bg-[#1A0F1F]">
            <button type="button" onClick={onClose} className="btn-secondary text-xs px-4 py-2"
              data-testid="add-product-cancel-btn">
              Cancel
            </button>
            <button type="submit" disabled={busy} className="btn-primary text-xs px-4 py-2 inline-flex items-center gap-1"
              data-testid="add-product-submit-btn">
              <Save size={12}/>{busy ? "Creating…" : "Create product"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
